import React, { useState, Fragment } from "react";
import {
  Box,
  IconButton,
  Typography,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  lighten,
  Backdrop,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";

const snakeToTitle = (text) =>
  text
    .replace(/^[-_]*(.)/, (_, c) => c.toUpperCase())
    .replace(/[-_]+(.)/g, (_, c) => " " + c.toUpperCase());

const CodeTable = ({
  title,
  rows,
  columns,
  loading,
  collapseSections,
  selectedRows,
  setSelectedRows,
  multiSelection = false,
}) => {
  const [expanded, setExpanded] = useState({});
  const theme = useTheme();

  const handleCheck = (row) => {
    setSelectedRows((prev) =>
      prev.some((x) => x.recommendation_id === row.recommendation_id)
        ? prev.filter((x) => x.recommendation_id !== row.recommendation_id)
        : [...prev, row]
    );
  };

  const isAllSelected =
    multiSelection && rows.length > 0 && selectedRows.length === rows.length;

  const handleSelectAllClick = () => {
    if (isAllSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(rows);
    }
  };

  const extendedColumns = [
    ...(multiSelection
      ? [
          {
            field: "select",
            headerName: (
              <Checkbox
                checked={isAllSelected}
                indeterminate={selectedRows.length > 0 && !isAllSelected}
                onChange={handleSelectAllClick}
                inputProps={{ "aria-label": "select all rows" }}
              />
            ),
            width: 40,
            renderCell: (params) => {
              const row = params.row;
              return (
                <Checkbox
                  checked={selectedRows.some(
                    (x) => x.recommendation_id === row.recommendation_id
                  )}
                  onChange={() => handleCheck(row)}
                />
              );
            },
          },
        ]
      : []),
    {
      field: "expand",
      headerName: "",
      width: 50,
      renderCell: (params) => {
        const id =
          params.row.id ||
          params.row.recommendation_id ||
          params.row.cpt_code ||
          params.row.icd_code;
        const isOpen = !!expanded[id];
        return (
          <IconButton
            size="small"
            onClick={() =>
              setExpanded((prev) => ({
                ...prev,
                [id]: !prev[id],
              }))
            }
          >
            {isOpen ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        );
      },
    },
    ...columns,
  ];

  return (
    <Box mb={2}>
      <Typography
        variant="subtitle2"
        gutterBottom
        fontWeight="bold"
        textAlign="center"
      >
        {title}
      </Typography>
      <TableContainer component={Paper} sx={{ border: "1px solid #ccc" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {extendedColumns.map((col) => (
                <TableCell
                  key={col.field}
                  sx={{
                    fontWeight: 600,
                    fontSize: 16,
                    backgroundColor: lighten(theme.palette.primary.main, 0.7),
                  }}
                  style={{ width: col.width }}
                >
                  {col.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows && rows.length > 0 && !loading ? (
              rows.map((row) => {
                const id =
                  row.id ||
                  row.recommendation_id ||
                  row.cpt_code ||
                  row.icd_code;
                return (
                  <Fragment key={id}>
                    <TableRow hover>
                      {extendedColumns.map((col) =>
                        col.renderCell ? (
                          <TableCell key={col.field}>
                            {col.renderCell({ row, id })}
                          </TableCell>
                        ) : (
                          <TableCell
                            key={col.field}
                            sx={{ fontSize: 16, fontWeight: 400 }}
                          >
                            {row[col.field]}
                          </TableCell>
                        )
                      )}
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={extendedColumns.length}
                        sx={{ padding: 0, border: 0 }}
                      >
                        <Collapse
                          in={!!expanded[id]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ bgcolor: "#f9f9fc", p: 2 }}>
                            {collapseSections.map((key) => (
                              <Box key={key} sx={{ mb: 1 }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={500}
                                  gutterBottom
                                >
                                  {snakeToTitle(key)}
                                </Typography>
                                <Typography variant="body2">
                                  {row[key] || "—"}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={extendedColumns.length}
                  align="center"
                  sx={{ fontStyle: "italic", py: 3 }}
                >
                  No data found
                </TableCell>
              </TableRow>
            )}
            <Backdrop
              open={loading}
              sx={{
                position: "absolute",
                zIndex: (theme) => theme.zIndex.drawer + 1,
                color: "#1976d2",
                backgroundColor: "rgba(255,255,255,0.6)",
              }}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CodeTable;
